"use client";

import Image from "next/image";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

type SpinnerLoadingProps = {
  /**
   * Espera antes de exibir o spinner. Hidratações rápidas (< 200ms) não devem
   * piscar um loader na tela; só sessões que realmente demoram o mostram.
   */
  delayMs?: number;
  label?: string;
  className?: string;
};

/**
 * Tela de espera enquanto a sessão é resolvida (hydrate: restore do token,
 * refresh silencioso e /me). É o que o usuário vê no lugar do flash da tela
 * de login ou de uma página em branco durante o redirecionamento.
 *
 * A animação segue o prompt de referência (loading.css): dois anéis de marca
 * contrarrotativos — externo no horário (1.4s), interno anti-horário (1.6s).
 */
export function SpinnerLoading({
  delayMs = 200,
  label = "Carregando...",
  className,
}: SpinnerLoadingProps) {
  const [visible, setVisible] = useState(delayMs <= 0);

  useEffect(() => {
    if (delayMs <= 0) return;
    const timer = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs]);

  return (
    <div
      data-slot="spinner-loading"
      role="status"
      aria-live="polite"
      className={cn(
        "fixed inset-0 z-[10001] grid place-items-center bg-background",
        className
      )}
    >
      {visible && (
        <div className="relative size-[106px]">
          <Image
            src="/assets/loading-1.png"
            alt=""
            aria-hidden="true"
            draggable={false}
            fill
            sizes="106px"
            className="animate-[dkro-spinner-cw_1.4s_linear_infinite] select-none"
          />
          <Image
            src="/assets/loading-2.png"
            alt=""
            aria-hidden="true"
            draggable={false}
            fill
            sizes="106px"
            className="animate-[dkro-spinner-ccw_1.6s_linear_infinite] select-none"
          />
        </div>
      )}
      <span className="sr-only">{label}</span>
    </div>
  );
}
